/**
 * WHAT THE APP ASKS THE MAP FOR, turned into where the camera goes.
 *
 * `V8Map` is the loop, the input and the seam to the rest of the app, and it is where every wiring
 * bug in this port has ended up — the spin hard-coded to zero, the wash canvas left at 300×150, the
 * label gate on invented numbers, the flight zooms taken as absolutes. Each of them was found by
 * driving a browser, because a loop that owns a canvas, a worker and a `requestAnimationFrame` is
 * not something a unit test can hold.
 *
 * The DECISIONS inside that loop are a different matter. This file is the first of them pulled out:
 * arithmetic over a request and the state of the camera, with nothing of the DOM in it, so the next
 * one of these can be caught by a test that runs in a second rather than by a screenshot.
 */

/**
 * HOW CLOSE AN ORDER ASKS FOR, in one of the two ways a zoom can be meant.
 *
 * A BARE NUMBER IS A MULTIPLE of the zoom that fits the whole field, and it is the default for a
 * reason: `flyTo(null, 1)` means "show me everything" and `flyTo(n, 4)` means "four times as close,
 * on that figure", so the same order means the same thing in a world of any size. Every beat of our
 * own arrival is written this way.
 *
 * `{ absolute }` IS A ZOOM ALREADY IN THIS WORLD'S UNITS, and exists for the numbers ported out of
 * their engine. Their floor and ours are not the same measurement — ours frames the crowd, theirs
 * frames the rim and rises with how little has been surveyed — so a multiple of "the floor" carries
 * across as a DIFFERENT PICTURE, while a rim-rescaled absolute carries across as the same one. It
 * is clamped to the floor when used, exactly as theirs is (`max(minZoom(), 0.6)`).
 *
 * Two spellings for one field rather than two fields, so an order cannot say both and mean neither.
 */
export type FlyZoom = number | { readonly absolute: number };

/** A request to fly somewhere, as the app words it. */
export interface FlyOrder {
  /** Where to point the camera, in world units — or null for the whole field. */
  to: { x: number; y: number } | null;
  /** How close. A bare number is a multiple of the floor; see `FlyZoom`. */
  zoom: FlyZoom;
  durMs: number;
}

/** Where the camera should end up. */
export interface CamTarget {
  x: number;
  y: number;
  z: number;
}

/**
 * The target for a flight.
 *
 * `whole` is the zoom that FITS THE FIELD and `floor` is the furthest out the reader may go. They
 * were one number for most of this port and are seven apart now — see `wholeField`. A bare multiple
 * is of the field, because that is what "show me everything" has to mean; the floor only clamps.
 *
 * A null destination is the middle of the field, which is where the whole field is framed from.
 */
export function flyTarget(order: FlyOrder, whole: number, floor: number): CamTarget {
  const asked = typeof order.zoom === 'number' ? whole * order.zoom : order.zoom.absolute;
  /* Never further out than the reader is allowed, which is what their own `max` is guarding. */
  return { x: order.to?.x ?? 0, y: order.to?.y ?? 0, z: Math.max(floor, asked) };
}

/**
 * The same order as a plain multiple of the floor, for a renderer that cannot take anything else.
 *
 * `MapCanvas` is the renderer this port replaces and it has no notion of an absolute zoom; an order
 * carrying one falls back to the whole field, which is the behaviour it shipped with. Here rather
 * than at its call site so that the day it is deleted, this goes with it.
 */
export function asFloors(order: FlyOrder): number {
  return typeof order.zoom === 'number' ? order.zoom : 1;
}

/**
 * Whether a flight should be taken instantly rather than travelled.
 *
 * Two reasons, and they are not the same reason. A reader who asked the system for less motion is
 * owed no animation at all; an order with no duration is one the caller has already decided is a
 * cut. Both end at the same place, and keeping them one expression is how a later change to either
 * ends up silently changing the other.
 */
export function flyCuts(order: FlyOrder, reduceMotion: boolean): boolean {
  return reduceMotion || order.durMs === 0;
}
